import { Search, X } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { PRIORITIES } from "../cardModel";
import { FilterSelect } from "../FilterSelect";
import {
  countActiveTaskFilters,
  DUE_FILTERS,
  parseTaskFilters,
  setSearchParam,
} from "../filters";
import { getSaleName, useActiveSales } from "../useActiveSales";
import { CardFilter } from "./CardFilter";
import { TASK_STATUSES } from "./taskStatuses";

const FILTER_KEYS = ["q", "user", "status", "priority", "card", "due"];
const SEARCH_DEBOUNCE_MS = 300;

export const TaskFilters = ({
  showStatus,
}: {
  /** The board already splits tasks by status. */
  showStatus: boolean;
}) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseTaskFilters(searchParams);
  const sales = useActiveSales();
  const any = translate("crm.operations.filters.any");

  const set = (key: string, value: string | null) =>
    setSearchParams((params) => setSearchParam(params, key, value), {
      replace: true,
    });

  // The search hits the server: wait for a pause in typing
  const [search, setSearch] = useState(filters.q);
  useEffect(() => {
    if (search === filters.q) return;
    const timeout = setTimeout(() => set("q", search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const clearFilters = () => {
    setSearch("");
    setSearchParams(
      (params) =>
        FILTER_KEYS.reduce(
          (next, key) => setSearchParam(next, key, null),
          params,
        ),
      { replace: true },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={translate("crm.operations.filters.search")}
          aria-label={translate("crm.operations.filters.search")}
          className="h-8 w-52 pl-8"
        />
      </div>
      <FilterSelect
        label={translate("crm.operations.filters.user")}
        anyLabel={translate("crm.operations.filters.all_users")}
        value={filters.salesId}
        onChange={(value) => set("user", value)}
        options={[
          ...(identity
            ? [
                {
                  value: String(identity.id),
                  label: translate("crm.operations.filters.mine"),
                },
              ]
            : []),
          ...sales
            .filter((sale) => sale.id !== identity?.id)
            .map((sale) => ({
              value: String(sale.id),
              label: getSaleName(sale),
            })),
        ]}
      />
      {showStatus && (
        <FilterSelect
          label={translate("crm.operations.filters.status")}
          anyLabel={`${translate("crm.operations.filters.status")}: ${any}`}
          value={filters.status}
          onChange={(value) => set("status", value)}
          options={TASK_STATUSES.map((status) => ({
            value: status,
            label: translate(`crm.operations.tasks.statuses.${status}`),
          }))}
        />
      )}
      <FilterSelect
        label={translate("crm.operations.filters.priority")}
        anyLabel={`${translate("crm.operations.filters.priority")}: ${any}`}
        value={filters.priority}
        onChange={(value) => set("priority", value)}
        options={PRIORITIES.map((priority) => ({
          value: priority,
          label: translate(`crm.operations.priorities.${priority}`),
        }))}
      />
      <CardFilter
        value={filters.contactId}
        onChange={(value) => set("card", value)}
      />
      <FilterSelect
        label={translate("crm.operations.filters.due")}
        anyLabel={`${translate("crm.operations.filters.due")}: ${any}`}
        value={filters.due}
        onChange={(value) => set("due", value)}
        options={DUE_FILTERS.map((due) => ({
          value: due,
          label: translate(`crm.operations.filters.dues.${due}`),
        }))}
      />
      {countActiveTaskFilters(filters) > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={clearFilters}
        >
          <X />
          {translate("crm.operations.filters.clear")}
        </Button>
      )}
    </div>
  );
};
